import React, { useEffect, useState } from 'react';
// Force rebuild for menu hierarchy update
import { VERSION_INFO } from '../constants/version';
import { fetchBackendVersion, getShortVersion } from '../utils/version';
import DashboardWidget from './DashboardWidget';
import ClassicScreenShell from './ClassicScreenShell';

interface ClassicMainMenuProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
  currentUser: any;
  onLogout?: () => void;
  headerRightContent?: React.ReactNode;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
  initialCategory?: string | null;
  onCategoryChange?: (category: string | null) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  disabled?: boolean;
  status?: 'implemented' | 'in-progress' | 'pending';
}

interface MenuSection {
  title: string;
  columns?: number;
  items: MenuItem[];
}

const ClassicMainMenu: React.FC<ClassicMainMenuProps> = ({
  onMenuSelect,
  tenantName,
  selectedYear,
  onYearChange,
  currentUser,
  onLogout,
  headerRightContent,
  apiFetch,
  currentTenantId,
  initialCategory,
  onCategoryChange,
}) => {
  const isSuperAdmin = (currentUser?.role ?? '').toLowerCase() === 'super_admin';
  const permissions: string[] = currentUser?.permissions || [];

  const hasPermission = (perm: string) => permissions.includes(perm);
  const canWriteJournal = hasPermission('journal_write');
  const canWriteMaster = hasPermission('master_write');
  const canManageBudget = hasPermission('budget_write');
  const canManageTenant = hasPermission('tenant_admin');
  const canManageUser = hasPermission('user_manage') || isSuperAdmin;
  const canManageSystem = hasPermission('system_admin') || isSuperAdmin;
  const [buildStamp, setBuildStamp] = useState(getShortVersion());

  useEffect(() => {
    let cancelled = false;
    fetchBackendVersion(apiFetch).then((remote) => {
      if (cancelled || !remote) return;
      const commit = (remote.git_commit || '').slice(0, 7);
      const deployed = remote.deploy_datetime || '';
      const parts: string[] = [];
      if (commit) parts.push(commit);
      if (deployed) parts.push(deployed);
      if (parts.length) setBuildStamp(parts.join(' · '));
    });
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  const sections: MenuSection[] = [
    {
      title: '日常処理',
      columns: 2,
      items: [
        { id: 'journal-entry', label: '会計伝票登録', icon: '📝', disabled: !canWriteJournal, status: 'implemented' },
        { id: 'journal-edit', label: '伝票の修正・削除', icon: '✏️', disabled: !canWriteJournal, status: 'implemented' },
        { id: 'monthly-summary', label: '月収支確認', icon: '📊', status: 'pending' },
        { id: 'general-ledger', label: '総勘定元帳', icon: '📖', status: 'pending' },
        { id: 'trial-balance', label: '試算表', icon: '⚖️', status: 'pending' },
        { id: 'sub-ledger', label: '補助元帳', icon: '🏷️', status: 'pending' },
        { id: 'report-output', label: '帳票出力', icon: '🖨️', status: 'in-progress' },
        { id: 'csv-output', label: 'CSV出力', icon: '📄', status: 'pending' },
        { id: 'cash-bank-ledger', label: '現預金出納帳', icon: '💴', status: 'pending' },
        { id: 'notes-management', label: '注記管理', icon: '🗒️', status: 'implemented' },
        { id: 'budget-management', label: '予算編成', icon: '📈', disabled: !canManageBudget, status: 'in-progress' },
        { id: 'budget-actual', label: '予算実績対比', icon: '📉', status: 'pending' },
      ],
    },
    {
      title: 'マスタ管理',
      columns: 2,
      items: [
        { id: 'kamoku-management', label: '勘定科目・補助科目マスタ', icon: '📁', disabled: !canWriteMaster, status: 'in-progress' },
        { id: 'hojo-master', label: '補助科目マスタ', icon: '🏷️', disabled: !canWriteMaster, status: 'in-progress' },
        { id: 'daikubun-management', label: '大区分マスタ', icon: '📂', disabled: !canWriteMaster, status: 'implemented' },
        { id: 'tekiyou-master', label: '摘要マスタ', icon: '🏷️', disabled: !canWriteMaster, status: 'pending' },
        { id: 'revenue-category', label: '収支区分設定', icon: '🔄', disabled: !canWriteMaster, status: 'pending' },
        { id: 'organization-info', label: '法人・施設情報', icon: '🏢', disabled: !canWriteMaster, status: 'implemented' },
        { id: 'fiscal-period', label: '会計期間設定', icon: '📅', disabled: !canWriteMaster, status: 'implemented' },
        { id: 'master-import', label: '科目マスタCSVインポート(M-06)', icon: '📥', status: 'implemented' },
      ],
    },
    {
      title: '保守・管理',
      columns: 2,
      items: [
        { id: 'carry-forward', label: '前期繰越', icon: '⏭️', disabled: !canManageTenant, status: 'pending' },
        { id: 'fixed-asset', label: '固定資産管理', icon: '🏢', disabled: !canWriteMaster, status: 'pending' },
        { id: 'real-estate-ledger', label: '不動産台帳', icon: '🏠', disabled: !canWriteMaster, status: 'implemented' },
        { id: 'system-close', label: '締切処理', icon: '🔒', disabled: !canManageTenant, status: 'pending' },
        { id: 'user-management', label: 'ユーザー管理', icon: '👥', disabled: !canManageUser, status: 'implemented' },
        { id: 'system-admin', label: 'テナント管理', icon: '⚙️', disabled: !canManageSystem, status: 'implemented' },
        { id: 'audit-logs', label: '監査ログ', icon: '🛡️', disabled: !hasPermission('audit_log_read') && !canManageSystem, status: 'implemented' },
        { id: 'backup-restore', label: 'データバックアップ・復元', icon: '💾', disabled: !canManageSystem, status: 'implemented' },
      ],
    },
    {
      title: '外部連携・分析',
      columns: 2,
      items: [
        { id: 'kokuho-ren', label: '国保連提出資料', icon: '📤', status: 'in-progress' },
        { id: 'xbrl-disclosure', label: '電子開示（WAM NET CSV）', icon: '💻', status: 'in-progress' },
        { id: 'audit-submission', label: '監査法人提出資料', icon: '📑', status: 'pending' },
        { id: 'cash-flow-analysis', label: '資金繰り分析', icon: '💹', status: 'implemented' },
        { id: 'department-report', label: '部門別収支報告', icon: '📊', status: 'implemented' },
        { id: 'management-analysis', label: '経営指標分析表', icon: '📈', status: 'implemented' },
      ],
    },
  ];

  // 2-Level Navigation State - 初期値を'日常処理'に設定して初回からメニューを表示(CR-009)
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(initialCategory || '日常処理');

  React.useEffect(() => {
    if (initialCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    if (onCategoryChange) {
      onCategoryChange(category);
    }
  };
  // Dashboard Visibility State (CR-002: On-demand display)
  const [showDashboard, setShowDashboard] = React.useState<boolean>(false);

  // Helper to find the currently selected section
  const currentSection = sections.find(s => s.title === selectedCategory);

  return (
    <ClassicScreenShell
      title="社会福祉法人 会計システム　メインメニュー"
      tenantName={tenantName}
      selectedYear={selectedYear}
      onYearChange={onYearChange}
      rightContent={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {headerRightContent || (
            <div style={{ fontSize: '10px', textAlign: 'right' }}>
              <div>System ver {VERSION_INFO.version}</div>
              <div title="git commit · deploy datetime">{buildStamp}</div>
              <div>DB ver 3</div>
              <div style={{ marginTop: '4px' }}>
                ユーザー: {currentUser?.username || 'admin'}
              </div>
            </div>
          )}
          <button
            onClick={onLogout}
            style={{
              fontSize: '11px',
              color: '#000',
              fontFamily: 'MS UI Gothic, sans-serif',
              backgroundColor: '#e0e0e0',
              border: '2px outset #e0e0e0',
              padding: '4px 8px',
              cursor: 'pointer',
            }}
            onMouseDown={(e) => (e.currentTarget.style.border = '2px inset #e0e0e0')}
            onMouseUp={(e) => (e.currentTarget.style.border = '2px outset #e0e0e0')}
            onMouseLeave={(e) => (e.currentTarget.style.border = '2px outset #e0e0e0')}
          >
            ログアウト
          </button>
        </div>
      }
    >
      <div
        className="classic-main-menu"
        style={{
          backgroundColor: '#f0f0f0',
          minHeight: '100vh',
          fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
          fontSize: '11px',
          position: 'relative',
        }}
      >
        {/* Dashboard Widget */}
        <div style={{ maxWidth: '900px', marginBottom: '16px' }}>
          <button
            onClick={() => setShowDashboard(!showDashboard)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              backgroundColor: showDashboard ? '#d0d0d0' : '#e0e0e0',
              border: showDashboard ? '2px inset #e0e0e0' : '2px outset #e0e0e0',
              cursor: 'pointer',
              fontFamily: "'MS UI Gothic', sans-serif",
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: showDashboard ? '16px' : '0'
            }}
            onMouseDown={(e) => {
              if (!showDashboard) e.currentTarget.style.border = '2px inset #e0e0e0';
            }}
            onMouseUp={(e) => {
              if (!showDashboard) e.currentTarget.style.border = '2px outset #e0e0e0';
            }}
            onMouseLeave={(e) => {
              if (!showDashboard) e.currentTarget.style.border = '2px outset #e0e0e0';
            }}
          >
            <span>経営ダッシュボード</span>
            <span>{showDashboard ? '▲ 閉じる' : '▼ 表示する'}</span>
          </button>
          {showDashboard && (
            <DashboardWidget apiFetch={apiFetch} currentTenantId={currentTenantId} />
          )}
        </div>

        {/* 2-Column Main Menu Layout */}
        <div style={{ display: 'flex', gap: '20px', maxWidth: '900px', alignItems: 'flex-start' }}>

          {/* Left Column: Major Categories */}
          <div style={{ flex: '0 0 250px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', borderBottom: '2px solid #0066cc', paddingBottom: '4px', marginBottom: '8px' }}>
              大区分
            </div>
            {sections.map((section) => {
              const isSelected = (selectedCategory || '日常処理') === section.title;
              return (
                <button
                  key={section.title}
                  onClick={() => handleCategorySelect(section.title)}
                  style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    textAlign: 'left',
                    backgroundColor: isSelected ? '#ffffff' : '#2E7D32', // 背景：白（選択時） / 緑（非選択時）
                    border: isSelected ? '2px inset #a0a0a0' : '2px outset #1b5e20',
                    cursor: 'pointer',
                    fontFamily: "'MS UI Gothic', sans-serif",
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: isSelected ? '#2E7D32' : '#ffffff' // 文字：緑（選択時） / 白（非選択時）
                  }}
                  onMouseDown={(e) => {
                    if (!isSelected) e.currentTarget.style.border = '2px inset #1b5e20';
                  }}
                  onMouseUp={(e) => {
                    if (!isSelected) e.currentTarget.style.border = '2px outset #1b5e20';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.border = '2px outset #1b5e20';
                  }}
                >
                  <span>{section.title}</span>
                  <span style={{ fontSize: '10px' }}>{isSelected ? '●' : '▶'}</span>
                </button>
              );
            })}
          </div>

          {/* Right Column: Medium Categories (Menu Items) */}
          <div style={{ flex: '1 1 auto' }}>
            <fieldset
              className="classic-fieldset"
              style={{ width: '100%', margin: 0, padding: '16px', minHeight: '300px' }}
            >
              <legend className="classic-legend" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {selectedCategory || '日常処理'} メニュー
              </legend>

              {currentSection && (
                <div
                  style={{
                    display: 'grid',
                    gap: '12px',
                    gridTemplateColumns: `repeat(${currentSection.columns ?? 2}, minmax(0, 1fr))`,
                  }}
                >
                  {currentSection.items.map(button => (
                    <button
                      key={button.id}
                      className="classic-button"
                      style={{
                        opacity: button.disabled ? 0.6 : 1,
                        cursor: button.disabled ? 'not-allowed' : 'pointer',
                        padding: '12px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        position: 'relative',
                        justifyContent: 'flex-start'
                      }}
                      onClick={() => !button.disabled && onMenuSelect(button.id)}
                      disabled={button.disabled}
                    >
                      <span style={{ fontSize: '16px', flexShrink: 0 }}>{button.icon}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <span style={{ fontWeight: 'bold' }}>{button.label}</span>
                        {button.status && (
                          <span style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            color: 'white',
                            backgroundColor: button.status === 'implemented' ? '#4CAF50' :
                              button.status === 'in-progress' ? '#FF9800' : '#888888',
                            marginTop: '2px',
                            fontWeight: 'normal'
                          }}>
                            {button.status === 'implemented' ? '✅ 実装済' :
                              button.status === 'in-progress' ? '🚧 対応中' : '⚪ 未着手'}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </fieldset>
          </div>

        </div>
      </div>
    </ClassicScreenShell>
  );
};

export default ClassicMainMenu;
